import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authService, UserProfile, LoginResponse, SendOtpResponse } from '../services/authService';
import { useUserTheme } from './UserThemeContext';

export interface MfaChallenge {
  mfaToken: string;
  maskedContact: string;
  mfaChannel: string;
  otpExpirySeconds: number;
}

export interface LoginOtpChallenge {
  challengeId: string;
  maskedEmail: string;
  expiresInSeconds: number;
}

export type LoginResult =
  | {
      success: false;
      message: string;
      errorCode?: string;
      approvalStatus?: string;
      patientStatus?: string;
      patientId?: string;
      programId?: string;
      patientName?: string;
    }
  | { success: true; requiresMfa: true; requiresOtp?: false; mfaChallenge: MfaChallenge; message: string }
  | { success: true; requiresMfa?: false; requiresOtp: true; otpChallenge: LoginOtpChallenge; message: string }
  | { success: true; requiresMfa?: false; requiresOtp?: false; user: UserProfile; message: string };

interface AuthContextType {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string, rememberMe: boolean) => Promise<LoginResult>;
  verifyMfa: (mfaToken: string, otpCode: string, rememberMe: boolean) => Promise<LoginResult>;
  verifyLoginOtp: (challengeId: string, otpCode: string) => Promise<LoginResult>;
  hcpApprovalLogin: (programId: string, patientId: string, approvalToken: string) => Promise<LoginResult>;
  resendLoginOtp: (challengeId: string) => Promise<{ success: boolean; message: string; data?: { challengeId: string; maskedEmail: string; expiresInSeconds: number } }>;
  sendOtp: (identifier: string, programCode?: string) => Promise<{ success: boolean; message: string; data?: SendOtpResponse }>;
  otpLogin: (identifier: string, otpCode: string, programCode?: string, rememberMe?: boolean) => Promise<LoginResult>;
  logout: () => Promise<void>;
  checkAuthStatus: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function buildLoginResult(result: { success: boolean; data: LoginResponse | null; message: string; errors?: string[]; correlationId?: string }): LoginResult {
  if (!result.success) {
    // Prefer specific error messages from the errors array
    const errorMessage = result.errors?.length ? result.errors[0] : result.message || 'Login failed';
    if (result.correlationId) {
      console.error(`[Login Error] correlationId: ${result.correlationId}`);
    }
    return { success: false, message: errorMessage };
  }
  const data = result.data!;
  if (data.requiresMfa) {
    return {
      success: true,
      requiresMfa: true,
      mfaChallenge: {
        mfaToken: data.mfaToken!,
        maskedContact: data.maskedContact!,
        mfaChannel: data.mfaChannel!,
        otpExpirySeconds: data.otpExpirySeconds ?? 300,
      },
      message: result.message,
    };
  }
  if (data.requiresOtp) {
    return {
      success: true,
      requiresOtp: true,
      otpChallenge: {
        challengeId: data.challengeId!,
        maskedEmail: data.maskedEmail!,
        expiresInSeconds: data.expiresInSeconds ?? 300,
      },
      message: result.message,
    };
  }
  return { success: true, user: data.user!, message: result.message };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();
  const { initTheme, clearTheme } = useUserTheme();

  // Check authentication status - verifies token is still valid
  const checkAuthStatus = useCallback((): boolean => {
    const isValid = authService.isAuthenticated();
    if (!isValid && user) {
      // Token has expired or been invalidated, clear state
      setUser(null);
    }
    return isValid;
  }, [user]);

  useEffect(() => {
    // Check if user is already logged in on mount
    const currentUser = authService.getCurrentUser();
    if (currentUser && authService.isAuthenticated()) {
      setUser(currentUser);
      // Restore theme preference on page refresh/reload
      initTheme(currentUser.userId, (currentUser.themePreference as 'light' | 'dark') || 'light');
    } else {
      // Clear any stale data
      setUser(null);
    }
    setIsLoading(false);
  }, [initTheme]);

  // Periodically check auth status to detect token expiry
  useEffect(() => {
    const interval = setInterval(() => {
      checkAuthStatus();
    }, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [checkAuthStatus]);

  const finalizeLogin = useCallback((userData: UserProfile) => {
    setUser(userData);
    initTheme(userData.userId, (userData.themePreference as 'light' | 'dark') || 'light');
  }, [initTheme]);

  const login = async (username: string, password: string, rememberMe: boolean): Promise<LoginResult> => {
    try {
      const result = await authService.login(username, password, rememberMe);
      const loginResult = buildLoginResult(result);
      if (loginResult.success && !loginResult.requiresMfa && !loginResult.requiresOtp) {
        finalizeLogin(loginResult.user);
      }
      return loginResult;
    } catch {
      return { success: false, message: 'An unexpected error occurred' };
    }
  };

  const verifyMfa = async (mfaToken: string, otpCode: string, rememberMe: boolean): Promise<LoginResult> => {
    try {
      const result = await authService.verifyMfa(mfaToken, otpCode, rememberMe);
      const loginResult = buildLoginResult(result);
      if (loginResult.success && !loginResult.requiresMfa && !loginResult.requiresOtp) {
        finalizeLogin(loginResult.user);
      }
      return loginResult;
    } catch {
      return { success: false, message: 'An unexpected error occurred' };
    }
  };

  const verifyLoginOtp = async (challengeId: string, otpCode: string): Promise<LoginResult> => {
    try {
      const result = await authService.verifyLoginOtp(challengeId, otpCode);
      const loginResult = buildLoginResult(result);
      if (loginResult.success && !loginResult.requiresMfa && !loginResult.requiresOtp) {
        finalizeLogin(loginResult.user);
      }
      return loginResult;
    } catch {
      return { success: false, message: 'An unexpected error occurred' };
    }
  };

  const hcpApprovalLogin = async (programId: string, patientId: string, approvalToken: string): Promise<LoginResult> => {
    try {
      const result = await authService.hcpApprovalLogin(programId, patientId, approvalToken);
      if (!result.success) {
        if (result.correlationId) {
          console.error(`[Approval Login Error] correlationId: ${result.correlationId}`);
        }

        return {
          success: false,
          message: result.message || 'Approval token login failed.',
          errorCode: result.data?.code || result.errors?.[0],
          approvalStatus: result.data?.approvalStatus,
          patientStatus: result.data?.patientStatus,
          patientId: result.data?.patientId,
          programId: result.data?.programId,
          patientName: result.data?.patientName,
        };
      }

      if (!result.data?.user || !result.data.token || !result.data.refreshToken) {
        return {
          success: false,
          message: result.message || 'Approval token login failed.',
          errorCode: result.data?.code || result.data?.status || result.errors?.[0],
          approvalStatus: result.data?.approvalStatus,
          patientStatus: result.data?.patientStatus,
          patientId: result.data?.patientId,
          programId: result.data?.programId,
          patientName: result.data?.patientName,
        };
      }

      finalizeLogin(result.data.user);
      return { success: true, user: result.data.user, message: result.message };
    } catch {
      return { success: false, message: 'An unexpected error occurred' };
    }
  };

  const resendLoginOtp = async (challengeId: string) => {
    try {
      const result = await authService.resendLoginOtp(challengeId);
      return { success: result.success, message: result.message, data: result.data ?? undefined };
    } catch {
      return { success: false, message: 'An unexpected error occurred' };
    }
  };

  const sendOtp = async (identifier: string, programCode?: string) => {
    try {
      const result = await authService.sendOtp(identifier, programCode);
      return { success: result.success, message: result.message, data: result.data ?? undefined };
    } catch {
      return { success: false, message: 'An unexpected error occurred' };
    }
  };

  const otpLogin = async (identifier: string, otpCode: string, programCode?: string, rememberMe = false): Promise<LoginResult> => {
    try {
      const result = await authService.otpLogin(identifier, otpCode, programCode, rememberMe);
      const loginResult = buildLoginResult(result);
      if (loginResult.success && !loginResult.requiresMfa && !loginResult.requiresOtp) {
        finalizeLogin(loginResult.user);
      }
      return loginResult;
    } catch {
      return { success: false, message: 'An unexpected error occurred' };
    }
  };

  const logout = useCallback(async () => {
    try {
      // Call server-side logout
      await authService.logout();
    } finally {
      // Clear React Query cache to remove any cached data
      queryClient.clear();
      
      // Reset user state
      setUser(null);

      // Reset theme to light for the next user
      clearTheme();
      
      // Clear any remaining cached data
      queryClient.invalidateQueries();
      queryClient.removeQueries();
    }
  }, [queryClient, clearTheme]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user && authService.isAuthenticated(),
        isLoading,
        login,
        verifyMfa,
        verifyLoginOtp,
        hcpApprovalLogin,
        resendLoginOtp,
        sendOtp,
        otpLogin,
        logout,
        checkAuthStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
